import React, { useState, useEffect, useCallback } from 'react';
import { Redirect } from 'react-router-dom';
import { css } from '@emotion/css';
import PageContainer from '../../components/structure/PageContainer';
import SettingsCard from '../../components/structure/SettingsCard';
import CustomScrollbars from '../../components/structure/CustomScrollbars';
import Loader from '../../components/general/Loader';
import { color_variants_main_bg, color_shades_darker } from '../../constants/colors';
import { FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId } from '../../constants/featureFlagIds';
import * as voicifyApi from '../../api';
import TagDisplayData from '../../models/configuration/TagDisplayData';
import CreateTagForm from './components/CreateTagForm';
import TagList, { TagListEntry } from './components/TagList';
import EditTagForm from './components/EditTagForm';

const CURRENT_ORG_KEY = "CURRENT_ORG";
const TAG_DATA_FILE = "tag_display_data.json";

const getFeatureFlagLabel = (featureFlagId: string): string => {
    if (featureFlagId === FoodOrderingFeatureFlagId) return 'Food Ordering';
    if (featureFlagId === HealthcareSchedulingFeatureFlagId) return 'Healthcare';
    return 'Unknown';
};

const AnalyticsManagement: React.FC = () => {
    const storedOrg = localStorage.getItem(CURRENT_ORG_KEY);
    let organizationId: string | null = null;

    if (storedOrg && storedOrg !== "undefined") {
        try {
            const parsed = JSON.parse(storedOrg);
            organizationId = parsed?.id || null;
        } catch {
            organizationId = null;
        }
    }

    const [foodOrderingTags, setFoodOrderingTags] = useState<TagListEntry[]>([]);
    const [healthcareTags, setHealthcareTags] = useState<TagListEntry[]>([]);
    const [sharedTagNames, setSharedTagNames] = useState<Set<string>>(new Set());
    const [isLoadingTags, setIsLoadingTags] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [editingTag, setEditingTag] = useState<TagListEntry | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Admin verification: probe the admin-protected getTagConfig endpoint
    // using SPANISH (a tag that always exists) to verify platform admin access.
    const [isAdminVerified, setIsAdminVerified] = useState<boolean | null>(null);

    useEffect(() => {
        let cancelled = false;
        const verifyAdmin = async () => {
            try {
                const result = await voicifyApi.getTagConfig('SPANISH', FoodOrderingFeatureFlagId);
                if (cancelled) return;
                setIsAdminVerified(result.resultType === 'Ok');
            } catch {
                if (!cancelled) {
                    setIsAdminVerified(false);
                }
            }
        };
        verifyAdmin();
        return () => { cancelled = true; };
    }, []);

    const loadTags = useCallback(async () => {
        if (!organizationId) return;

        setIsLoadingTags(true);
        setLoadError(null);

        try {
            // The GET endpoint requires an applicationId for permission checking.
            // First, get the org's applications to use the first one for the GET call.
            const appsResult = await voicifyApi.getApplicationsForOrg(organizationId);
            if (appsResult.resultType !== 'Ok' || !appsResult.data || appsResult.data.length === 0) {
                setLoadError('No applications found for this organization. Cannot load tag data.');
                setIsLoadingTags(false);
                return;
            }

            const applicationId = appsResult.data[0].id;
            const result = await voicifyApi.getTagConfigData(applicationId, TAG_DATA_FILE);

            if (result.resultType !== 'Ok' || !result.data) {
                setLoadError('Failed to load tag display data.');
                setIsLoadingTags(false);
                return;
            }

            let displayData: TagDisplayData;
            if (typeof result.data === 'string') {
                displayData = JSON.parse(result.data);
            } else {
                displayData = result.data as any;
            }

            const tagDisplayNames = displayData.tagDisplayNames || {};

            // Build sets for each vertical
            const foodTagSet = new Set(displayData.foodOrderingTags || []);
            const healthcareTagSet = new Set(displayData.healthcareTags || []);

            // Find tags that appear in both verticals
            const shared = new Set<string>();
            foodTagSet.forEach(t => {
                if (healthcareTagSet.has(t)) shared.add(t);
            });
            setSharedTagNames(shared);

            // Helper to determine sentiment for a tag
            const getSentiment = (tagName: string): string => {
                if ((displayData.positiveTags || []).includes(tagName)) return 'positive';
                if ((displayData.negativeTags || []).includes(tagName)) return 'negative';
                if ((displayData.cautionTags || []).includes(tagName)) return 'caution';
                if ((displayData.neutralTags || []).includes(tagName)) return 'neutral';
                return 'neutral';
            };

            const buildEntry = (tagName: string, featureFlagId: string): TagListEntry => ({
                tagName,
                displayName: tagDisplayNames[tagName] || tagName,
                sentiment: getSentiment(tagName),
                featureFlagId,
                featureFlagLabel: getFeatureFlagLabel(featureFlagId)
            });

            // Build separate lists for each vertical
            const foodEntries: TagListEntry[] = [];
            foodTagSet.forEach(tagName => {
                foodEntries.push(buildEntry(tagName, FoodOrderingFeatureFlagId));
            });
            foodEntries.sort((a, b) => a.displayName.localeCompare(b.displayName));
            setFoodOrderingTags(foodEntries);

            const healthcareEntries: TagListEntry[] = [];
            healthcareTagSet.forEach(tagName => {
                healthcareEntries.push(buildEntry(tagName, HealthcareSchedulingFeatureFlagId));
            });
            healthcareEntries.sort((a, b) => a.displayName.localeCompare(b.displayName));
            setHealthcareTags(healthcareEntries);
        } catch (err) {
            setLoadError('An error occurred loading tag data.');
        }

        setIsLoadingTags(false);
    }, [organizationId]);

    useEffect(() => {
        loadTags();
    }, [loadTags]);

    const handleDelete = async (tagName: string): Promise<boolean> => {
        setIsDeleting(true);
        try {
            const result = await voicifyApi.removeTag(tagName);
            if (result.resultType === 'Ok') {
                await loadTags();
                return true;
            }
            return false;
        } catch {
            return false;
        } finally {
            setIsDeleting(false);
        }
    };

    const handleEditSuccess = () => {
        setEditingTag(null);
        loadTags();
    };

    const handleCreateSuccess = () => {
        loadTags();
    };

    // While verifying admin status, show a loading indicator
    if (isAdminVerified === null) {
        return (
            <div className={pageWrapperStyle}>
                <PageContainer>
                    <div className={centeredContentStyle}>
                        <Loader />
                    </div>
                </PageContainer>
            </div>
        );
    }

    // If not a platform admin, redirect to the main page
    if (!isAdminVerified) {
        return <Redirect to="/v" />;
    }

    if (!organizationId) {
        return (
            <div className={pageWrapperStyle}>
                <PageContainer>
                    <div className={centeredContentStyle}>
                        <SettingsCard title="Analytics Management">
                            <p className={errorTextStyle}>
                                No organization found. Please select an organization first.
                            </p>
                        </SettingsCard>
                    </div>
                </PageContainer>
            </div>
        );
    }

    return (
        <div className={pageWrapperStyle}>
            <PageContainer>
                <CustomScrollbars className={scrollStyle}>
                    <div className={centeredContentStyle}>
                        <h3 className={pageTitleStyle}>Analytics Management</h3>
                        <p className={pageDescriptionStyle}>
                            Internal tools for managing analytics tag configuration.
                        </p>

                        <SettingsCard title="Create Tag" subtitle="Add a new tag to the analytics review system">
                            <CreateTagForm onSuccess={handleCreateSuccess} />
                        </SettingsCard>

                        <div className={sectionSpacerStyle} />

                        {editingTag ? (
                            <SettingsCard title={`Edit Tag: ${editingTag.displayName}`} subtitle={`Editing ${editingTag.tagName} — tag name and feature flag cannot be changed`}>
                                <EditTagForm
                                    tagName={editingTag.tagName}
                                    featureFlagId={editingTag.featureFlagId}
                                    initialDisplayName={editingTag.displayName}
                                    initialSentiment={editingTag.sentiment}
                                    onSuccess={handleEditSuccess}
                                    onCancel={() => setEditingTag(null)}
                                />
                            </SettingsCard>
                        ) : (
                            <SettingsCard
                                title="Existing Tags"
                                subtitle="Tags grouped by vertical — tags shared across both will appear in each table"
                                isLoading={isLoadingTags}
                            >
                                {loadError ? (
                                    <p className={errorTextStyle}>{loadError}</p>
                                ) : (
                                    <TagList
                                        foodOrderingTags={foodOrderingTags}
                                        healthcareTags={healthcareTags}
                                        sharedTagNames={sharedTagNames}
                                        isLoading={isLoadingTags}
                                        onEdit={(tag) => setEditingTag(tag)}
                                        onDelete={handleDelete}
                                        isDeleting={isDeleting}
                                    />
                                )}
                            </SettingsCard>
                        )}
                    </div>
                </CustomScrollbars>
            </PageContainer>
        </div>
    );
};

const pageWrapperStyle = css`
    background: ${color_variants_main_bg};
    height: 100%;
    width: 100%;
`;

const scrollStyle = css`
    height: calc(100vh - 32px);
`;

const centeredContentStyle = css`
    max-width: 1100px;
    margin: 0 auto;
    padding: 32px 24px 120px 24px;
`;

const pageTitleStyle = css`
    font-size: 23px;
    margin-bottom: 4px;
`;

const pageDescriptionStyle = css`
    font-size: 14px;
    color: ${color_shades_darker};
    margin-bottom: 24px;
`;

const errorTextStyle = css`
    color: #D8414D;
    font-size: 14px;
`;

const sectionSpacerStyle = css`
    height: 24px;
`;

export default AnalyticsManagement;
